import { useState } from "react";
import React from 'react';
import { useFetchData } from "services/queries";
import Box from '@mui/material/Box';
import {
    useGridApiRef,
} from '@mui/x-data-grid-premium';
import EditableDataGrid from "components/EditableDataGrid/EditableDataGrid";
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import EditIcon from '@mui/icons-material/Edit';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { GRID_AGGREGATION_ROOT_FOOTER_ROW_ID } from '@mui/x-data-grid-premium';



const USDcurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
});

const BRLcurrencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
});

const Payments = ({ formData }) => {
    const resourcePath = 'payment';
    const relatedResources = `payment/?sales_order=${formData.id}`;
    const [tableData, setTableData] = useState([]);
    const apiRef = useGridApiRef();


    const [openModal, setOpenModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    const handleOpenModal = (imageUrl) => {
        // if is pdf, open in new tab
        if (imageUrl.includes('.pdf')) {
            // open new tab with the pdf
            window.open(imageUrl, '_blank');
            return;
        }

        setSelectedImage(imageUrl);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedImage(null);
    };

    const handleImageUpload = (event, params) => {
        console.log("params: ", params)
        const file = event.target.files[0];
        const updatedRows = tableData.map(row => {
            if (row.id === params.id) {
                return { ...row, file: file };
            }
            return row;
        });
        setTableData(() => updatedRows);
        apiRef.current.setEditCellValue({ id: params.id, field: 'file', value: file });
        console.log("updatedRows: ", updatedRows)
    };




    const actions = {
        create: true,
        edit: true,
        delete: false, // Removed as per requirement - payments should not be deletable from UI
        // Add custom edit check for system-managed payments
        isCellEditable: (params) => {
            // Don't allow editing system-managed payments except for certain fields
            if (params.row.is_system_managed) {
                // Allow editing only date and file for system-managed
                return ['date', 'file'].includes(params.field);
            }
            return true;
        }
    }

    const newRowTemplate = {
        contract_number: '',
        intermediary: '',
        date: null,
        usd_value: 0,
        brl_value: 0,
        sales_order: formData.id,
        isNew: true,
    }

    const columnsDataItems = [
        // contract_number
        {
            field: 'contract_number',
            headerName: 'Contract Number',
            editable: true,
            groupable: true,
            aggregable: false,
            flex: 1,
            minWidth: 190,
        },
        // intermediary
        {
            field: 'intermediary',
            headerName: 'Intermediary',
            editable: true,
            groupable: false,
            aggregable: false,
            flex: 1,
            minWidth: 140,
        },
        // date
        {
            field: 'date',
            headerName: 'Date',
            editable: true,
            groupable: false,
            aggregable: false,
            flex: 1,
            minWidth: 100,
            type: 'date',
            valueGetter: (value) => value ? new Date(value + "T12:00:00") : null,  // Converts string to Date object for editing
            valueSetter: (value, row) => {
                const oldValue = row.date;
                if (value instanceof Date) {
                    return { ...row, date: value.toISOString().split('T')[0] };  // Converts Date object back to string in 'YYYY-MM-DD' format
                }
                return { ...row, date: oldValue };  // Keeps the old value if the new value isn't a Date instance
            },
            valueFormatter: (value) => {
                if (value == null) {
                    return '';
                }
                //   format the date to 'YYYY-MM-DD'
                return value.toISOString().split('T')[0];
            }
        },
        // usd_value
        {
            field: 'usd_value',
            headerName: 'USD Value',
            editable: true,
            groupable: false,
            aggregable: true,
            type: 'number',
            flex: 1,
            minWidth: 80,
            valueFormatter: (value) => {
                if (value) {
                    return USDcurrencyFormatter.format(value);
                }
                else {
                    return 0;
                }
            },
            valueGetter: (value) => (value ? parseFloat(value) : 0),
        },
        // brl_value
        {
            field: 'brl_value',
            headerName: 'BRL Value',
            editable: true,
            groupable: false,
            aggregable: true,
            type: 'number',
            flex: 1,
            minWidth: 150,
            valueFormatter: (value) => (value ? BRLcurrencyFormatter.format(value) : 0),
            valueGetter: (value) => (value ? parseFloat(value) : 0),
        },
        // dollar_exchange
        {
            field: 'dollar_exchange',
            headerName: 'Exchange Rate',
            editable: true,
            groupable: false,
            aggregable: false,
            type: 'number',
            flex: 1,
            minWidth: 120,
            valueFormatter: (value) => (value ? value.toFixed(5) : '-'),
            valueGetter: (value) => (value ? parseFloat(value) : null),
        },
        // category
        {
            field: 'category',
            headerName: 'Category',
            editable: true,
            type: 'singleSelect',
            valueOptions: [
                { value: null, label: 'Uncategorized' },
                { value: 'INVOICE', label: 'Invoice' },
                { value: 'FREIGHT_FEES', label: 'Freight Fees' },
                { value: 'TAX_DUTIES', label: 'Tax & Duties' }
            ],
            flex: 1,
            minWidth: 130,
            renderCell: (params) => {
                if (params.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) {
                    return null;
                }
                const value = params.value;
                const displayValue = params.row.category_display || 'Uncategorized';

                // Google-like clean chip design
                return (
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '16px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            backgroundColor: value ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                            color: value ? '#1976d2' : '#666',
                        }}
                    >
                        {displayValue}
                    </Box>
                );
            }
        },
        // system-managed indicator
        {
            field: 'is_system_managed',
            headerName: 'Source',
            editable: false,
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) => {
                if (params.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) {
                    return null;
                }
                const isSystemManaged = params.value;

                return (
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                        }}
                    >
                        {isSystemManaged ? (
                            <>
                                <AutoModeIcon sx={{ fontSize: 16, color: '#666' }} />
                                <Typography variant="caption" color="textSecondary">
                                    Template
                                </Typography>
                            </>
                        ) : (
                            <>
                                <EditIcon sx={{ fontSize: 16, color: '#666' }} />
                                <Typography variant="caption" color="textSecondary">
                                    Manual
                                </Typography>
                            </>
                        )}
                    </Box>
                );
            }
        },
        // file upload
        {
            field: 'file',
            headerName: 'Receipt',
            editable: true,
            groupable: false,
            aggregable: false,
            valueGetter: (value) => {
                return value;
            },
            renderCell: (params) => {
                const imageUrl = params.row.file_url;
                if (params.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) {
                    return [];
                }
                return (
                    <div>
                        {imageUrl ? (
                            <IconButton color="primary" onClick={() => handleOpenModal(imageUrl)}>
                                <VisibilityIcon />
                            </IconButton>
                        ) : <Tooltip title={"Double click to edit"} >
                            <IconButton>
                                <VisibilityOffIcon />
                            </IconButton>
                        </Tooltip>}
                    </div>
                );
            },
            renderEditCell: (params) => {
                return (
                    <div>
                        <Button
                            variant="contained"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            style={{ textTransform: 'none' }}
                        >
                            <input
                                type="file"
                                accept="image/*,.pdf"
                                hidden
                                onChange={(event) => handleImageUpload(event, params)}
                            />
                        </Button>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">

            <h3 className="pt-[5px] text-2xl font-bold text-navy-700 dark:text-white">Payments</h3>
            <Box>
                <EditableDataGrid
                    columns={columnsDataItems}
                    resourcePath={resourcePath}
                    tableData={tableData}
                    setTableData={setTableData}
                    relatedResources={relatedResources}
                    apiRef={apiRef}
                    onBeforeRowUpdate={async (newRow, oldRow) => {
                        // Validate category restrictions
                        if (!newRow.is_system_managed &&
                            newRow.category === 'INVOICE' &&
                            formData.payment_term_template) {
                            throw new Error('Manual invoice payments are not allowed when using payment templates');
                        }
                        return newRow;
                    }}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                        pinnedColumns: { left: ['actions'] },
                        aggregation: {
                            model: {
                                usd_value: 'sum',
                                brl_value: 'sum',
                            },
                        },
                    }}
                    autosizeOptions={{
                        columns: ['contract_number', 'intermediary', 'date', 'usd_value', 'brl_value', 'category', 'is_system_managed'],
                        includeOutliers: true,
                        includeHeaders: true,
                    }}
                    actions={actions}
                    autosizeOnMount={true}
                    newRowTemplate={newRowTemplate}
                />
            </Box>

            <Modal open={openModal} onClose={handleCloseModal}>
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'white',
                    padding: 16,
                    borderRadius: 8,
                    outline: 'none',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    maxWidth: '90%',
                    maxHeight: '90%',
                    overflow: 'auto',
                    width: 'auto', // Explicitly set width or maxWidth
                    height: 'auto', // Explicitly set height or maxHeight
                }}>
                    <IconButton
                        style={{ position: 'absolute', top: 8, right: 8 }}
                        onClick={handleCloseModal}
                    >
                        <CloseIcon />
                    </IconButton>
                    {selectedImage && (
                        <img
                            src={selectedImage}
                            alt="Uploaded"
                            style={{ maxWidth: '100%', maxHeight: 'auto', objectFit: 'contain' }}
                        />
                    )}
                </div>
            </Modal>
        </div>
    );
}

export default Payments